/**
 * Dispatches to the correct field component based on field_type.
 */

import type { CustomizationField } from '../lib/types';
import { TextInputField } from './fields/TextInputField';
import { TextAreaField } from './fields/TextAreaField';
import { SelectField } from './fields/SelectField';
import { ToggleField } from './fields/ToggleField';
import { FileUploadField } from './fields/FileUploadField';

interface FieldRendererProps {
  field: CustomizationField;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  forcedValue?: string | null;
  required?: boolean;
}

export function CustomizationFieldRenderer({
  field,
  value,
  onChange,
  error,
  disabled = false,
  forcedValue,
  required,
}: FieldRendererProps) {
  const isRequired = required ?? !!field.required;

  switch (field.field_type) {
    case "Toggle":
      return (
        <ToggleField
          label={field.label}
          value={value}
          onChange={onChange}
          addonPrice={field.addon_price}
          disabled={disabled}
          forcedValue={forcedValue}
          error={error}
        />
      );

    case "Text Input":
      return (
        <TextInputField
          label={field.label}
          value={value}
          onChange={onChange}
          maxCharacters={field.max_characters || undefined}
          required={isRequired}
          disabled={disabled}
          forcedValue={forcedValue}
          error={error}
        />
      );

    case "Text Area":
      return (
        <TextAreaField
          label={field.label}
          value={value}
          onChange={onChange}
          maxCharacters={field.max_characters || undefined}
          required={isRequired}
          disabled={disabled}
          forcedValue={forcedValue}
          error={error}
        />
      );

    case "Single Select":
    case "Multi Select":
      return (
        <SelectField
          label={field.label}
          value={value}
          onChange={onChange}
          options={field.options}
          addonPrice={field.addon_price}
          required={isRequired}
          disabled={disabled}
          forcedValue={forcedValue}
          error={error}
        />
      );

    case "File Upload":
      return (
        <FileUploadField
          label={field.label}
          value={value}
          onChange={onChange}
          allowedFileTypes={field.allowed_file_types || undefined}
          maxFileSize={field.max_file_size || 5}
          required={isRequired}
          disabled={disabled}
          error={error}
        />
      );

    default:
      return null;
  }
}
